/*
 * ActivationWizard — Phase 49 (Readiness Assistant)
 * =================================================
 *
 * RESPONSIBILITIES (intentionally narrow)
 *   1. Show the live readiness checklist for the shipment (5 items).
 *   2. For each missing item, surface a single "Go to <module>" button that
 *      navigates the operator to the correct workflow.
 *   3. When all items are satisfied, expose Activate Monitoring which calls
 *      POST /api/lifecycle/shipments/{id}/activate.
 *
 * NON-RESPONSIBILITIES (deliberately removed)
 *   - This component does NOT create shipments, facilities, geofences,
 *     contacts, or notification rules.
 *   - Each of those flows now lives in its own module:
 *       Create Shipment      -> ShipmentForm
 *       Facilities/Geofences -> Operational Parties (Admin Tools)
 *       Stakeholders         -> Operational Parties (Admin Tools)
 *       Thresholds           -> Threshold Settings (top-nav)
 *       Event Orchestration  -> Notification Rules (Admin Tools)
 *
 * Backend contract
 *   GET  /api/lifecycle/shipments/{id}/readiness  -> { ready, score, items[] }
 *   POST /api/lifecycle/shipments/{id}/activate   -> { ok, lifecycle_state }
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lightning, CheckCircle, Circle, X, ArrowRight,
  MapPinArea, UsersThree, Thermometer, Bell, BookOpen,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Maps readiness item keys -> the navigation target dispatched on the
// `holotrak:nav` event bus, plus the human-readable button label.
// `tab` is set for items that belong on the *current shipment* (in-shipment
// tab swap, dispatched via `holotrak:ship-tab-nav`). Items without a `tab`
// open the global admin modal via `holotrak:nav`.
const ITEM_NAV = {
  facilities:    { tab: "parties",        target: "facilities",          label: "Go to Parties",              Icon: MapPinArea },
  stakeholders:  { tab: "parties",        target: "parties",             label: "Go to Parties",              Icon: UsersThree },
  thresholds:    {                        target: "thresholds",          label: "Go to Thresholds",           Icon: Thermometer },
  orchestration: {                        target: "event_orchestration", label: "Go to Event Orchestration",  Icon: Bell },
};

function navigateTo(target, tab, onClose) {
  // If the readiness item belongs on the current ShipmentDetail page, swap
  // tabs in place and dismiss the wizard so the user isn't trapped behind
  // the modal. Otherwise fall back to the global admin modal bus.
  if (tab) {
    window.dispatchEvent(new CustomEvent("holotrak:ship-tab-nav", { detail: { tab } }));
    if (typeof onClose === "function") onClose();
    return;
  }
  window.dispatchEvent(new CustomEvent("holotrak:nav", { detail: { target } }));
  if (typeof onClose === "function") onClose();
}

export default function ActivationWizard({ shipment, onClose, onActivated }) {
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!shipment?.id) return;
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/lifecycle/shipments/${shipment.id}/readiness`);
      setReadiness(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Could not load readiness");
    } finally {
      setLoading(false);
    }
  }, [shipment?.id]);

  useEffect(() => { reload(); }, [reload]);

  // Re-check readiness whenever the operator returns to this modal (e.g.
  // after they navigated away to Parties to add a facility).
  useEffect(() => {
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [reload]);

  const handleActivate = async () => {
    if (!readiness?.ready) {
      toast.error("Complete every readiness item before activating.");
      return;
    }
    setActivating(true);
    const t = toast.loading("Activating monitoring…");
    try {
      const res = await axios.post(`${API}/lifecycle/shipments/${shipment.id}/activate`);
      toast.success("Shipment ACTIVE — dispatch enabled.", { id: t });
      onActivated?.(res.data?.lifecycle_state || "active", res.data);
    } catch (e) {
      // The backend returns a precise 400 with the missing-items list.
      const detail = e.response?.data?.detail || e.message || "Activation failed.";
      setError(detail);
      toast.error(detail, { id: t });
      // Refresh — maybe something changed under us.
      reload();
    } finally {
      setActivating(false);
    }
  };

  const items = readiness?.items || [];

  return (
    <div
      className="fixed inset-0 z-[2200] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="activation-wizard"
    >
      <Card
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border-zinc-800 shadow-2xl w-[640px] max-w-full max-h-[88vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-800 flex items-start justify-between bg-gradient-to-r from-teal-500/10 to-transparent">
          <div className="flex items-start gap-2.5">
            <Lightning size={18} weight="duotone" className="text-teal-400 mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-zinc-100">Activation Readiness</h2>
              <p className="text-[11px] text-zinc-500 truncate max-w-[420px]">
                {shipment?.name || "Shipment"} · review and activate when all checks pass
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid="wizard-close">
            <X size={16} />
          </button>
        </div>

        {/* Score banner */}
        <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-900/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ScoreRing score={readiness?.score ?? 0} ready={!!readiness?.ready} />
            <div>
              <div className="text-xs text-zinc-400">Readiness Score</div>
              <div className="text-lg font-semibold text-zinc-50 font-mono">
                {loading ? "…" : `${readiness?.score ?? 0}%`}
              </div>
            </div>
          </div>
          <a
            href="https://coldchain.holotrak.com/help/quickstart"
            target="_blank" rel="noreferrer"
            className="text-[11px] text-teal-400 hover:text-teal-300 flex items-center gap-1"
            data-testid="wizard-help-link"
          >
            <BookOpen size={12} /> Setup guide
          </a>
        </div>

        {/* Readiness checklist */}
        <div className="flex-1 overflow-y-auto p-5 space-y-2">
          {loading && (
            <div className="text-xs text-zinc-500 italic">Loading readiness…</div>
          )}
          {!loading && items.map((item) => (
            <ReadinessRow key={item.key} item={item} onClose={onClose} />
          ))}
          {error && (
            <div className="mt-3 px-3 py-2 rounded-sm border border-red-700/60 bg-red-500/10 text-[11px] text-red-300"
              data-testid="wizard-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-800 flex items-center justify-between bg-zinc-900/40">
          <Button
            variant="outline"
            onClick={reload}
            disabled={loading}
            className="border-zinc-700 text-zinc-300 text-xs h-8"
            data-testid="wizard-recheck"
          >
            Re-check
          </Button>
          <Button
            disabled={!readiness?.ready || activating}
            onClick={handleActivate}
            className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs h-8 font-semibold disabled:opacity-40"
            data-testid="wizard-activate"
          >
            {activating ? "Activating…" : (<>
              <Lightning size={11} weight="fill" className="mr-1" /> Activate Monitoring
            </>)}
          </Button>
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------- */

function ReadinessRow({ item, onClose }) {
  const ok = !!item.ok;
  const nav = ITEM_NAV[item.key];
  const Icon = nav?.Icon;
  return (
    <div
      className={`flex items-start justify-between gap-3 px-3 py-2.5 rounded-sm border ${
        ok ? "border-emerald-800/40 bg-emerald-500/5" : "border-amber-700/40 bg-amber-500/5"
      }`}
      data-testid={`readiness-row-${item.key}`}
    >
      <div className="flex items-start gap-2.5 flex-1 min-w-0">
        {ok
          ? <CheckCircle size={16} weight="fill" className="text-emerald-400 mt-0.5 shrink-0" />
          : <Circle size={16} className="text-amber-400 mt-0.5 shrink-0" />}
        <div className="min-w-0">
          <div className="text-sm font-medium text-zinc-100 flex items-center gap-1.5">
            {Icon && <Icon size={12} className="text-zinc-400" />}
            {item.label}
          </div>
          <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">
            {ok ? "Configured." : item.hint}
          </div>
        </div>
      </div>
      {!ok && nav && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigateTo(nav.target, nav.tab, onClose)}
          className="h-7 text-[10px] border-teal-700/60 text-teal-300 hover:bg-teal-500/10 shrink-0"
          data-testid={`readiness-goto-${item.key}`}
        >
          {nav.label} <ArrowRight size={11} className="ml-1" />
        </Button>
      )}
    </div>
  );
}

function ScoreRing({ score, ready }) {
  const r = 14, c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  const color = ready ? "#2AC19F" : score >= 60 ? "#f59e0b" : "#ef4444";
  return (
    <svg width="36" height="36" viewBox="0 0 36 36" className="-rotate-90">
      <circle cx="18" cy="18" r={r} stroke="#27272a" strokeWidth="4" fill="none" />
      <circle cx="18" cy="18" r={r} stroke={color} strokeWidth="4" fill="none"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        style={{ transition: "stroke-dashoffset 0.45s ease" }} />
    </svg>
  );
}
